import { Box, Text, useColorModeValue } from '@chakra-ui/react';
import React, { useCallback, useEffect, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  getPlainTextLength,
  getVisibleText,
} from '../../utils/textConversion';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
  isReadOnly?: boolean;
  minHeight?: string;
  maxHeight?: string;
  variant?: 'fixedCap' | 'fillParent';
  // Length validation props
  enableLengthLimit?: boolean;
  maxChars?: number;
  enforceStrategy?: 'warnOnly' | 'block' | 'truncate';
  countStrategy?: 'visibleText' | 'plain';
  nearLimitRatio?: number;
  counterMode?: 'post' | 'pre';
  showOverAttempt?: boolean;
  showCounter?: boolean;
  // Legacy prop for backward compatibility
  maxLength?: number;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  label,
  placeholder = 'Start typing...',
  required = false,
  error,
  isReadOnly = false,
  minHeight = '12rem',
  maxHeight = '40rem',
  variant = 'fixedCap',
  // Length validation props with defaults
  enableLengthLimit = false,
  maxChars = 1024,
  enforceStrategy = 'warnOnly',
  countStrategy = 'visibleText',
  nearLimitRatio = 0.95,
  counterMode = 'post',
  showOverAttempt = true,
  showCounter = true,
  // Legacy prop for backward compatibility
  maxLength,
}) => {
  // Use legacy maxLength if provided, otherwise use maxChars
  const effectiveMaxChars = maxLength ?? maxChars;
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const focusBorderColor = useColorModeValue('blue.500', 'blue.300');
  const labelColor = useColorModeValue('gray.700', 'gray.200');
  const errorColor = useColorModeValue('red.500', 'red.300');
  const hoverBorderColor = useColorModeValue('gray.300', 'gray.500');

  // Value is always HTML (from Quill's onChange or metadata.htmlContent).
  // Never call stringToHtml here — the value has never been stored as markdown.
  const htmlValue = value;

  // Character count using visible text length (HTML tags excluded)
  const getCharCount = useCallback((html: string): number => {
    if (countStrategy === 'plain') return getPlainTextLength(html);
    return getVisibleText(html).length;
  }, [countStrategy]);

  // Initialize counts from the current value so the counter is correct on first render
  const [rawCount, setRawCount] = useState(() => getCharCount(value));
  const [storedCount, setStoredCount] = useState(() => getCharCount(value));
  const [isOverLimit, setIsOverLimit] = useState(
    () => enableLengthLimit && getCharCount(value) >= effectiveMaxChars
  );
  const [validationError, setValidationError] = useState<string | undefined>(
    () => {
      const initial = getCharCount(value);
      return enableLengthLimit && initial >= effectiveMaxChars
        ? `Maximum ${effectiveMaxChars} characters allowed (backend constraint)`
        : undefined;
    }
  );

  // Sync counts when value changes externally (form reset, initial load)
  useEffect(() => {
    const currentCount = getCharCount(value);
    setStoredCount(currentCount);
    setRawCount(currentCount);

    const isOver = enableLengthLimit && currentCount >= effectiveMaxChars;
    setIsOverLimit(isOver);
    setValidationError(
      isOver
        ? `Maximum ${effectiveMaxChars} characters allowed (backend constraint)`
        : undefined
    );
  }, [value, effectiveMaxChars, enableLengthLimit, getCharCount]);

  const handleChange = (htmlContent: string) => {
    // Post-process HTML to add security attributes to external links
    let processedHtml = htmlContent;
    // Add rel="noopener noreferrer" to links with target="_blank" for security
    processedHtml = processedHtml.replace(
      /<a\s+([^>]*target=["']_blank["'][^>]*)>/gi,
      (match, attrs) => {
        // Check if rel attribute already exists
        if (!/rel=["'][^"']*["']/i.test(attrs)) {
          return `<a ${attrs} rel="noopener noreferrer">`;
        }
        // If rel exists but doesn't include noopener, add it
        if (!/rel=["'][^"']*noopener[^"']*["']/i.test(attrs)) {
          return match.replace(
            /rel=["']([^"']*)["']/i,
            'rel="$1 noopener noreferrer"'
          );
        }
        return match;
      }
    );

    // Always compute raw count (attempted length) for display and color logic
    const attemptedRawCount = getCharCount(processedHtml);
    setRawCount(attemptedRawCount);

    // Skip validation if length limit is disabled
    if (!enableLengthLimit) {
      setStoredCount(attemptedRawCount);
      onChange(processedHtml);
      return;
    }

    // Apply enforcement strategy
    if (attemptedRawCount > effectiveMaxChars) {
      switch (enforceStrategy) {
        case 'block':
          // Don't update content - ignore additional input
          // Keep stored count unchanged, but raw count shows attempted input
          return;

        case 'truncate': {
          // Truncate to maxChars visible characters
          const visibleText = getVisibleText(processedHtml);
          const truncatedText = visibleText.slice(0, effectiveMaxChars);
          setStoredCount(effectiveMaxChars);
          onChange(truncatedText);
          return;
        }

        case 'warnOnly':
          // Allow overage but show error state
          setStoredCount(attemptedRawCount);
          onChange(processedHtml);
          return;

        default:
          // Default to block behavior
          return;
      }
    }

    // Content is within limits, proceed normally
    setStoredCount(attemptedRawCount);
    onChange(processedHtml);
  };

  const modules = {
    toolbar: [['bold', 'italic', 'strike', 'link']],
  };
  const formats = ['bold', 'italic', 'strike', 'link'];

  const wrapperHeight = variant === 'fillParent' ? '100%' : maxHeight;
  const wrapperMaxHeight =
    variant === 'fixedCap' ? maxHeight : undefined;

  // Counter color logic with precise thresholds (using rawCount for immediate red state)
  const getCounterColor = () => {
    if (!enableLengthLimit) return 'gray.500';

    const threshold = effectiveMaxChars * nearLimitRatio; // Configurable threshold for amber

    if (rawCount >= effectiveMaxChars) return errorColor;
    if (rawCount >= threshold) return 'orange.500';
    return 'gray.500';
  };

  const getCounterWeight = () => {
    if (!enableLengthLimit) return '400';
    return rawCount >= effectiveMaxChars ? '600' : '400';
  };

  // Get display count based on counter mode
  const getDisplayCount = () => {
    return counterMode === 'pre' ? rawCount : storedCount;
  };

  // Get over-attempt display
  const getOverAttemptDisplay = () => {
    if (
      !showOverAttempt ||
      !enableLengthLimit ||
      rawCount <= effectiveMaxChars
    ) {
      return null;
    }
    const overCount = rawCount - effectiveMaxChars;
    return ` (+${overCount} over)`;
  };

  return (
    <Box>
      {label && (
        <Text
          mb="0.5rem"
          fontSize="0.875rem"
          fontWeight="500"
          color={labelColor}
        >
          {label}{' '}
          {required && <span style={{ color: errorColor }}>*</span>}
        </Text>
      )}

      {/* Main editor container with fixed height */}
      <Box
        border="1px solid"
        borderColor={isOverLimit ? errorColor : borderColor}
        borderRadius="lg"
        bg={bgColor}
        _focusWithin={{
          borderColor: isOverLimit ? errorColor : focusBorderColor,
          boxShadow: `0 0 0 1px ${
            isOverLimit ? errorColor : focusBorderColor
          }`,
          transition: 'all 0.2s ease-in-out',
        }}
        transition="all 0.2s ease-in-out"
        _hover={{
          borderColor: isOverLimit ? errorColor : hoverBorderColor,
        }}
        overflow="hidden" // Ensure content doesn't spill out
        height={wrapperHeight}
        maxHeight={wrapperMaxHeight}
        minHeight={minHeight}
      >
        <Box
          className="rte"
          height="100%"
          display="flex"
          flexDirection="column"
        >
          <ReactQuill
            value={htmlValue}
            onChange={handleChange}
            placeholder={placeholder}
            modules={modules}
            formats={formats}
            readOnly={isReadOnly}
            style={{
              fontSize: '0.875rem',
              height: '100%',
            }}
            theme="snow"
            aria-invalid={isOverLimit}
            aria-describedby={
              (validationError ? 'length-error' : '') +
              (showCounter ? ' char-counter' : '')
            }
          />
        </Box>
      </Box>

      {/* Footer content outside the bordered container */}
      <Box mt="0.25rem">
        {error && (
          <Text
            color={errorColor}
            fontSize="0.75rem"
            mb="0.25rem"
            fontWeight="500"
          >
            {error}
          </Text>
        )}

        {validationError && (
          <Text
            id="length-error"
            color={errorColor}
            fontSize="0.75rem"
            mb="0.25rem"
            fontWeight="500"
          >
            {validationError}
          </Text>
        )}

        {/* Character counter */}
        {showCounter && (
          <Box textAlign="right">
            <Text
              id="char-counter"
              fontSize="xs"
              color={getCounterColor()}
              fontWeight={getCounterWeight()}
              display="inline"
            >
              {enableLengthLimit ? `${getDisplayCount()} / ${effectiveMaxChars}` : getDisplayCount()}
            </Text>
            {getOverAttemptDisplay() && (
              <Text
                fontSize="xs"
                color="gray.400"
                fontWeight="400"
                display="inline"
                ml="0.25rem"
              >
                {getOverAttemptDisplay()}
              </Text>
            )}
          </Box>
        )}
      </Box>

      <style>{`
        /* Single-scroll-owner flex layout */
        .rte {
          display: flex !important;
          flex-direction: column !important;
          box-sizing: border-box !important;
          height: 100% !important;
        }

        /* Toolbar stays fixed */
        .rte .ql-toolbar {
          flex: 0 0 auto !important;
          box-sizing: border-box !important;
          border: none !important;
          border-bottom: 1px solid ${borderColor} !important;
          background: ${useColorModeValue(
            'gray.50',
            'gray.700'
          )} !important;
          border-radius: 0.5rem 0.5rem 0 0 !important;
          padding: 0.75rem !important;
        }

        /* Container fills remaining space but does NOT scroll */
        .rte .ql-container {
          flex: 1 1 auto !important;
          box-sizing: border-box !important;
          overflow: hidden !important;
          border: none !important;
          font-family: inherit !important;
          min-height: 0 !important; /* Allow flex shrinking */
        }

        /* The only scroll owner */
        .rte .ql-editor {
          height: 100% !important;
          box-sizing: border-box !important;
          overflow-y: auto !important;
          overflow-x: hidden !important;
          scrollbar-gutter: stable both-edges !important;
          line-height: 1.6 !important;
          font-size: 0.875rem !important;
          word-break: break-word !important;
          overflow-wrap: break-word !important;
          white-space: pre-wrap !important;
          padding: 1rem 1.25rem 1rem 1rem !important; /* Extra right padding for caret */
          min-height: 0 !important; /* Allow flex shrinking */
        }

        .rte .ql-editor.ql-blank::before {
          color: ${useColorModeValue(
            'gray.400',
            'gray.500'
          )} !important;
          font-style: italic !important;
          font-size: 0.875rem !important;
        }

        .rte .ql-editor p {
          margin-bottom: 0.75rem !important;
        }

        .rte .ql-editor p:last-child {
          margin-bottom: 0 !important;
        }

        .rte .ql-editor ul, .rte .ql-editor ol {
          margin-bottom: 0.75rem !important;
          padding-left: 1.5rem !important;
        }

        .rte .ql-editor li {
          margin-bottom: 0.25rem !important;
        }

        .rte .ql-editor blockquote {
          border-left: 3px solid ${useColorModeValue(
            'blue.300',
            'blue.500'
          )} !important;
          padding-left: 1rem !important;
          margin: 0.75rem 0 !important;
          font-style: italic !important;
          color: ${useColorModeValue(
            'gray.600',
            'gray.300'
          )} !important;
        }

        .rte .ql-editor h1, .rte .ql-editor h2, .rte .ql-editor h3 {
          margin: 1rem 0 0.5rem 0 !important;
          font-weight: bold !important;
        }

        .rte .ql-editor h1 {
          font-size: 1.25rem !important;
        }

        .rte .ql-editor h2 {
          font-size: 1.125rem !important;
        }

        .rte .ql-editor h3 {
          font-size: 1rem !important;
        }

        .rte .ql-toolbar button {
          margin-right: 0.25rem !important;
          border-radius: 0.25rem !important;
          transition: all 0.2s ease-in-out !important;
        }

        .rte .ql-toolbar button:hover {
          background: ${useColorModeValue(
            'blue.50',
            'blue.900'
          )} !important;
          color: ${useColorModeValue(
            'blue.600',
            'blue.300'
          )} !important;
        }

        .rte .ql-toolbar button.ql-active {
          background: ${useColorModeValue(
            'blue.500',
            'blue.600'
          )} !important;
          color: white !important;
        }

        .rte .ql-toolbar .ql-stroke {
          stroke: currentColor !important;
        }

        .rte .ql-toolbar .ql-fill {
          fill: currentColor !important;
        }

        .rte .ql-editor a {
          color: #3182ce !important;
          text-decoration: underline !important;
        }

        .rte .ql-editor a:hover {
          color: #2c5282 !important;
          text-decoration: underline !important;
        }

        .rte .ql-editor a:visited {
          color: #553c9a !important;
        }
      `}</style>
    </Box>
  );
};

export default RichTextEditor;
